
import React, { useCallback, useEffect, useState } from "react";
import { Heart, Send, ChevronDown, MessageCircle } from "lucide-react";
import { supabase } from "./lib/supabase";

const MAX_REPLY_LENGTH = 300;
const MAX_AUTHOR_LENGTH = 80;

export default function SendLoveBack({
  noteId,
  isStarter = false,
}) {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const [author, setAuthor] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackIsError, setFeedbackIsError] = useState(false);

  const loadReplies = useCallback(async () => {
    if (!noteId || isStarter) return;

    const { data, error } = await supabase
      .from("love_message_replies")
      .select("id, message, author_name, created_at")
      .eq("love_message_id", noteId)
      .eq("moderation_status", "approved")
      .order("created_at", { ascending: true });

    if (error) throw error;

    setReplies(data || []);
  }, [noteId, isStarter]);

  useEffect(() => {
    if (!noteId || isStarter) {
      setReplies([]);
      return;
    }

    let active = true;

    async function fetchReplies() {
      setLoading(true);
      setLoadError("");

      try {
        const { data, error } = await supabase
          .from("love_message_replies")
          .select("id, message, author_name, created_at")
          .eq("love_message_id", noteId)
          .eq("moderation_status", "approved")
          .order("created_at", { ascending: true });

        if (error) throw error;

        if (active) {
          setReplies(data || []);
        }
      } catch (error) {
        console.error("Unable to load replies:", error);

        if (active) {
          setLoadError("Replies couldn't be loaded right now.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    fetchReplies();

    return () => {
      active = false;
    };
  }, [noteId, isStarter]);

  async function handleSubmit(event) {
    event.preventDefault();

    if (submitting || !noteId || isStarter) return;

    const cleanMessage = message.trim();
    const cleanAuthor = author.trim();

    if (
      cleanMessage.length < 1 ||
      cleanMessage.length > MAX_REPLY_LENGTH ||
      cleanAuthor.length > MAX_AUTHOR_LENGTH
    ) {
      setFeedbackIsError(true);
      setFeedback("Please check your reply and try again.");
      return;
    }

    setSubmitting(true);
    setFeedback("");
    setFeedbackIsError(false);

    try {
      const { error } = await supabase.rpc(
        "submit_love_message_reply",
        {
          p_love_message_id: noteId,
          p_message: cleanMessage,
          p_author_name: cleanAuthor || null,
        }
      );

      if (error) throw error;

      setMessage("");
      setAuthor("");
      setExpanded(false);
      setFeedbackIsError(false);
      setFeedback("Your love has been shared! ❤️");

      try {
        await loadReplies();
      } catch (refreshError) {
        console.error("Unable to refresh replies:", refreshError);
        setLoadError(
          "Your reply was sent, but the comments couldn't refresh. Please reload the page."
        );
      }
    } catch (error) {
      console.error("Unable to submit reply:", error);
      setFeedbackIsError(true);
      setFeedback(
        "Your reply couldn't be sent. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (isStarter || !noteId) return null;

  return (
    <div className="sendLoveBack sendLoveBackModern">
      {replies.length > 0 && (
        <div className="loveReplies">
          <div className="loveRepliesHeading">
            <MessageCircle size={15} />
            <span>
              {replies.length}{" "}
              {replies.length === 1 ? "reply" : "replies"}
            </span>
          </div>

          <div className="loveRepliesList">
            {replies.map((reply) => (
              <div className="loveReply" key={reply.id}>
                <p className="loveReplyMessage">
                  {reply.message}
                </p>
                <p className="loveReplyAuthor">
                  — {reply.author_name || "Anonymous"}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <p className="loveReplyStatus" role="status">
          Gathering replies...
        </p>
      )}

      {loadError && (
        <p className="loveReplyStatus" role="status">
          {loadError}
        </p>
      )}

      <button
        type="button"
        className={`sendLoveBackToggle modernReplyToggle ${
          expanded ? "isExpanded" : ""
        }`}
        onClick={() => {
          setExpanded((current) => !current);
          setFeedback("");
          setFeedbackIsError(false);
        }}
        aria-expanded={expanded}
        aria-controls={`send-love-back-${noteId}`}
      >
        <span className="modernReplyToggleLabel">
          <Heart size={16} />
          <span>Send Love Back</span>
        </span>

        <ChevronDown
          size={17}
          className="modernReplyChevron"
        />
      </button>

      {expanded && (
        <div
          className="sendLoveBackPanel modernReplyPanel"
          id={`send-love-back-${noteId}`}
        >
          <p className="modernReplyIntro">
            A little kindness can travel both ways.
          </p>

          <form onSubmit={handleSubmit}>
            <label htmlFor={`reply-message-${noteId}`}>
              Your reply
            </label>

            <textarea
              id={`reply-message-${noteId}`}
              value={message}
              onChange={(event) =>
                setMessage(event.target.value)
              }
              placeholder="Write something kind..."
              maxLength={MAX_REPLY_LENGTH}
              rows={3}
              required
              disabled={submitting}
            />

            <p className="loveReplyCounter">
              {MAX_REPLY_LENGTH - message.length} characters left
            </p>

            <label htmlFor={`reply-author-${noteId}`}>
              From (optional)
            </label>

            <input
              id={`reply-author-${noteId}`}
              type="text"
              value={author}
              onChange={(event) =>
                setAuthor(event.target.value)
              }
              placeholder="Your name or Anonymous"
              maxLength={MAX_AUTHOR_LENGTH}
              disabled={submitting}
            />

            <p className="sendLoveBackPrivacy">
              Please avoid sharing private contact information.
              Replies are published publicly.
            </p>

            <button
              type="submit"
              className="sendLoveBackSubmit"
              disabled={submitting || !message.trim()}
            >
              <Send size={15} />
              {submitting ? "Sending..." : "Send your reply"}
            </button>
          </form>
        </div>
      )}

      {feedback && (
        <p
          className={
            feedbackIsError
              ? "loveReplyFeedback loveReplyFeedbackError"
              : "loveReplyFeedback"
          }
          role="status"
        >
          {feedback}
        </p>
      )}
    </div>
  );
}
